/**
 * Afriqahome API Types
 * 
 * Definitioner för databas-typer och frontend-typer.
 * FIXED: Added tier to agent, updated_at to DbProfile
 */

// agencyBranding is deliberately structural and imports nothing from here, so
// this stays a one-way dependency.
import type { ResolvedBranding } from './agencyBranding';

// ============================================
// DATABASE TYPES (snake_case, matches Supabase)
// ============================================

export interface DbListing {
  id: string;
  agent_id: string;
  title: string;
  slug?: string;  // URL-safe slug for SEO-friendly URLs
  address: string | null;
  city: string;
  neighborhood: string | null;
  price: number;
  currency: string;
  property_type: 'apartment' | 'villa' | 'townhouse' | 'bungalow' | 'plot' | 'new_development';
  beds: number | null;
  baths: number | null;
  beds_max?: number | null;  // For New Developments: max bedrooms in range
  baths_max?: number | null; // For New Developments: max bathrooms in range
  area_sqm: number | null;
  description: string | null;
  lat: number | null;
  lng: number | null;
  status: 'draft' | 'published' | 'sold';
  /**
   * AGENCY-11 (D3): the agency that owned this listing AT PUBLICATION — an
   * `agencies(id)`, never a profile id. Ambiguous on its own; always read with
   * `agency_stamped_at`, which says whether it means anything.
   *
   * Server-written only: the `trg_stamp_listing_agency` trigger overwrites any
   * value sent from the client. NEVER include this in a create/update payload —
   * it will be discarded. Optional because several listing queries select
   * explicit column lists that predate the column.
   */
  agency_id?: string | null;
  /**
   * AGENCY-11 (D3): when this listing was stamped, i.e. first published. The
   * AUTHORITY for `agency_id` — set means the stamp is final and is to be
   * trusted even when `agency_id` is null ("published independent"); null means
   * the row is legacy or still a draft and branding resolves live (AGENCY-08).
   * Trigger-owned and frozen once set; forging it from the client is refused.
   */
  agency_stamped_at?: string | null;
  spotlight_until: string | null;
  image_url: string | null;
  images?: string[] | null;  // Array of image URLs for gallery
  created_at: string;
  updated_at: string;
  // Sold fields
  final_price: number | null;
  sold_at: string | null;
  days_on_market: number | null;
  // Amenity fields (snake_case to match Supabase)
  pool?: boolean;
  gym?: boolean;
  garage?: boolean;
  garden?: boolean;
  security?: boolean;
  air_con?: boolean;
  balcony?: boolean;
  furnished?: boolean;
  fireplace?: boolean;
  borehole?: boolean;
  near_tarmac?: boolean;
  title_ready?: boolean;
  staff_quarters?: boolean;
  backup_generator?: boolean;
  // Trust & Legal
  title_deed_type?: 'freehold' | 'leasehold' | 'sectional' | null;
  leasehold_years_remaining?: number | null;
  encumbrance_free?: boolean;
  // Security (specific)
  gated_community?: boolean;
  electric_fence?: boolean;
  cctv?: boolean;
  security_guards?: boolean;
  alarm_system?: boolean;
  // Water
  mains_water?: boolean;
  water_storage_litres?: number | null;
  // Power
  solar?: boolean;
  // Connectivity
  fiber_internet?: boolean;
  // Building
  floor_level?: number | null;
  parking_spaces?: number | null;
  lift?: boolean;
  en_suite?: boolean;
  kids_play_area?: boolean;
  // Land
  road_access_type?: 'tarmac' | 'murram' | 'all_weather' | 'earth' | null;
  distance_from_tarmac_m?: number | null;
  zoning?: 'residential' | 'commercial' | 'agricultural' | 'mixed_use' | 'industrial' | null;
  beaconed?: boolean;
  survey_approved?: boolean;
  // Service charge for monthly cost calculator
  service_charge?: number | null;
  // New Development units (JSONB column)
  units?: DbPropertyUnit[];
  isParentListing?: boolean;
}

// Unit type for New Development listings
export interface DbPropertyUnit {
  id: string;
  unitNumber: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  size: number;
  status: 'Available' | 'Sold' | 'Reserved';
  floor?: string; // Floor or floor range, e.g. "1", "1-12", "Ground-12"
  units_available?: number;
}

/**
 * Convert frontend PropertyType enum value to database-safe property_type
 * Handles spaces, capitalization, and legacy value mappings
 *
 * DB check constraint expects: 'apartment' | 'villa' | 'townhouse' | 'bungalow' | 'plot' | 'new_development'
 */
export function propertyTypeToDb(frontendType: string): DbListing['property_type'] {
  const mapping: Record<string, DbListing['property_type']> = {
    // Valid DB values (direct pass-through)
    'apartment': 'apartment',
    'villa': 'villa',
    'townhouse': 'townhouse',
    'bungalow': 'bungalow',
    'plot': 'plot',
    'new_development': 'new_development',
    // Frontend enum values (PascalCase display labels)
    'Apartment': 'apartment',
    'Villa': 'villa',
    'Townhouse': 'townhouse',
    'Bungalow': 'bungalow',
    'Plot': 'plot',
    'New Development': 'new_development',
    // Edge cases and legacy mappings
    'new development': 'new_development',
    // Legacy values → correct DB values (backwards compatibility)
    'house': 'bungalow',
    'House': 'bungalow',
    'land': 'plot',
    'Land': 'plot',
    'commercial': 'new_development',
    'Commercial': 'new_development',
  };

  return mapping[frontendType] || 'apartment';
}

export interface DbProfile {
  id: string;
  slug?: string | null;
  email: string | null;
  full_name: string | null;
  phone: string | null;
  role: 'buyer' | 'agent' | 'admin';
  tier: 'ignite' | 'elevate' | 'dominate' | null;
  is_verified: boolean;
  avatar_url: string | null;
  background_image: string | null;
  /**
   * AGENCY-FAS1: an `agencies(id)` — NOT a profile id.
   *
   * Until 2026-08-01 this was a self-reference into profiles: an "agency" was
   * another agent's row. Anything that compares this to a user id is wrong, and
   * wrong silently, because both sides are uuids. The client only ever treats it
   * as opaque (stored, and tested for truthiness in lib/teamInvite.ts), which is
   * why the repoint needed no component changes.
   */
  agency_id: string | null;
  /**
   * Legacy free-text company name and logo, kept as the read-fallback. Every
   * card, the agent page and the agent directory still read these; AGENCY-FAS1
   * deliberately does not drop them, and the 5 profiles that hold a logo with no
   * name keep both exactly as they are.
   */
  agency_name: string | null;
  agency_logo_url: string | null;
  /** AGENCY-FAS1: which agency is paying for this seat. Server-written only. */
  sponsored_by_agency_id?: string | null;
  /** AGENCY-FAS1: end of this seat's own sponsorship clock. Server-written only. */
  sponsored_until?: string | null;
  /** AGENCY-FAS1: this profile's role inside `agency_id`. Server-written only. */
  agency_role?: 'owner' | 'admin' | 'agent' | null;
  is_independent: boolean | null;
  location: string | null;
  bio: string | null;
  created_at: string;
  updated_at?: string;
  /**
   * APPLE-1.2-A consent evidence (migration add_tos_accepted_at.sql:4). Present
   * on every `select=*` read and written by all four profile-creation paths,
   * but missing from this interface until AGENCY-20 needed to read it —
   * AuthCallback had been declaring it on its own local `ExistingProfile`
   * instead (AuthCallback.tsx:28).
   */
  tos_accepted_at?: string | null;
  // Subscription lifecycle fields
  subscription_expires_at: string | null;
  /**
   * ONBOARD-01 added 'trial' — the 3-month welcome tier, granted at signup and
   * paid for by nobody. 'sponsored' (an agency paying for a seat) has been a
   * live value since AGENCY-FAS1 and was missing from this union; both are
   * stated now so a `switch` over this type sees the real vocabulary.
   */
  subscription_status:
    | 'none'
    | 'active'
    | 'trial'
    | 'grace_period'
    | 'expired'
    | 'sponsored'
    | null;
  /**
   * ONBOARD-01: when the one-time welcome tier was granted. NULL = never
   * granted, which is what makes an OAuth agent eligible to claim it
   * (claim_welcome_tier) and what stops the selector reappearing afterwards.
   * Server-written only — the bug045 policy rejects a client UPDATE of it.
   */
  welcome_granted_at?: string | null;
  renewal_reminder_sent: boolean | null;
  spotlight_credits?: number;
  // CHAT-06 Fas 2 (DEL B): buyer push pre-permission tracking
  notification_pre_permission_state?: 'asked' | 'granted' | 'denied' | 'dismissed' | null;
  notification_pre_permission_at?: string | null;
}

export interface DbSavedProperty {
  id: string;
  user_id: string;
  listing_id: string;
  created_at: string;
}

export interface DbTransaction {
  id: string;
  user_id: string;
  type: 'subscription' | 'spotlight';
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  metadata: Record<string, any> | null;
  created_at: string;
}

// ============================================
// FRONTEND TYPES (camelCase, for React)
// ============================================

export interface Property {
  id: string;
  slug?: string;  // URL-safe slug for SEO-friendly URLs
  agentId: string;
  title?: string;  // SEO-optimized title (e.g., "2 Bed Apartment on Off Argwings Kodhek Road, Kilimani")
  address: string;
  city: string;
  neighborhood?: string;
  price: number;
  currency: string;
  rooms: number;
  roomsMax?: number | null;     // For New Developments: max bedrooms in range
  bathrooms: number;
  bathroomsMax?: number | null; // For New Developments: max bathrooms in range
  area: number;
  type: string;
  status: 'draft' | 'published' | 'sold';
  image: string;
  images: string[];
  description: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  // Direct lat/lng for form state and easier access
  lat?: number | null;
  lng?: number | null;
  features: string[];
  isSpotlight: boolean;
  agent: {
    slug?: string;
    name: string;
    image: string;
    phone: string;
    tier?: 'ignite' | 'elevate' | 'dominate';
    agency_name?: string;
    agency_logo_url?: string;
    is_independent?: boolean;
  };
  // Amenity fields (camelCase for frontend)
  pool: boolean;
  gym: boolean;
  garage: boolean;
  garden: boolean;
  security: boolean;
  airCon: boolean;
  balcony: boolean;
  furnished: boolean;
  fireplace: boolean;
  borehole: boolean;
  nearTarmac: boolean;
  titleReady: boolean;
  staffQuarters: boolean;
  backupGenerator: boolean;
  // Trust & Legal
  titleDeedType?: 'freehold' | 'leasehold' | 'sectional' | null;
  leaseholdYearsRemaining?: number | null;
  encumbranceFree?: boolean;
  // Security
  gatedCommunity?: boolean;
  electricFence?: boolean;
  cctv?: boolean;
  securityGuards?: boolean;
  alarmSystem?: boolean;
  // Water
  mainsWater?: boolean;
  waterStorageLitres?: number | null;
  // Power
  solar?: boolean;
  // Connectivity
  fiberInternet?: boolean;
  // Building
  floorLevel?: number | null;
  parkingSpaces?: number | null;
  lift?: boolean;
  enSuite?: boolean;
  kidsPlayArea?: boolean;
  // Land
  roadAccessType?: 'tarmac' | 'murram' | 'all_weather' | 'earth' | null;
  distanceFromTarmacM?: number | null;
  zoning?: 'residential' | 'commercial' | 'agricultural' | 'mixed_use' | 'industrial' | null;
  beaconed?: boolean;
  surveyApproved?: boolean;
  // Service charge for monthly cost calculator
  serviceCharge?: number;
  // Sold fields
  finalPrice?: number;
  soldDate?: string;
  daysOnMarket?: number;
  createdAt?: string;
  // Optional fields
  nextOpenHouse?: string;
  developmentId?: string;
  isParentListing?: boolean;
  units?: DbPropertyUnit[];
}

export interface User {
  id: string;
  slug?: string;
  email: string;
  role: 'buyer' | 'agent' | 'admin';
  name: string;
  phone?: string;
  tier?: 'ignite' | 'elevate' | 'dominate';
  isVerified?: boolean;
  avatarUrl?: string;
  backgroundImage?: string;
  agencyId?: string;
  agencyName?: string;
  agencyLogoUrl?: string;
  isIndependent?: boolean;
  location?: string;
  bio?: string;
  // Subscription lifecycle fields
  subscriptionExpiresAt?: string;
  subscriptionStatus?: 'none' | 'active' | 'trial' | 'grace_period' | 'expired' | 'sponsored';
  /** ONBOARD-01 — see DbProfile.welcome_granted_at. */
  welcomeGrantedAt?: string;
  /** ONBOARD-05 — profile creation time, read to decide welcome-offer eligibility. */
  createdAt?: string;
  renewalReminderSent?: boolean;
  spotlightCredits?: number;
}

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  user: User;
  expiresAt: number;
}

// ============================================
// TRANSFORM FUNCTIONS
// ============================================

const DEFAULT_PROPERTY_IMAGE = '/placeholder-property.svg';

/**
 * Omvandla DbListing till Property (för frontend).
 *
 * `branding` (AGENCY-11) is the agency this SPECIFIC listing must be attributed
 * to, already resolved by lib/api/agencyBranding.ts. It is a separate argument
 * rather than something read off `agentProfile` because the publish-time stamp
 * is per-listing: the same agent can own one listing stamped to the firm they
 * used to be at and another stamped to their current one, so no single mutated
 * profile row can carry both. Omitted → the agent's own profile fields, which
 * is the pre-AGENCY-11 behaviour every non-listing-surface caller still wants.
 */
export function dbListingToProperty(
  listing: DbListing,
  agentProfile?: DbProfile,
  branding?: ResolvedBranding
): Property {
  return {
    id: listing.id,
    slug: listing.slug,
    agentId: listing.agent_id,
    // Title is SEO-optimized (e.g., "2 Bed Apartment on Road, Neighborhood")
    // Address is the short address/road name
    title: listing.title || undefined,
    address: listing.address || listing.title || '',
    city: listing.city,
    neighborhood: listing.neighborhood || '',
    price: listing.price,
    currency: listing.currency || 'KES',
    rooms: listing.beds || 0,
    roomsMax: listing.beds_max || null,
    bathrooms: listing.baths || 0,
    bathroomsMax: listing.baths_max || null,
    area: listing.area_sqm || 0,
    type: listing.property_type || 'apartment',
    status: listing.status || 'published',
    image: listing.image_url || DEFAULT_PROPERTY_IMAGE,
    images: listing.images?.length
      ? listing.images
      : (listing.image_url ? [listing.image_url] : [DEFAULT_PROPERTY_IMAGE]),
    description: listing.description || '',
    coordinates: {
      lat: listing.lat || -1.286389,
      lng: listing.lng || 36.817223
    },
    // Direct lat/lng for easier access
    lat: listing.lat,
    lng: listing.lng,
    features: ['Available'],
    isSpotlight: listing.spotlight_until ? new Date(listing.spotlight_until) > new Date() : false,
    agent: {
      slug: agentProfile?.slug || undefined,
      name: agentProfile?.full_name || 'Agent',
      image: agentProfile?.avatar_url || 'https://ui-avatars.com/api/?name=Agent',
      phone: agentProfile?.phone || '+254 700 000 000',
      tier: agentProfile?.tier || undefined,
      // AGENCY-11: the listing's own resolved branding when the caller supplied
      // it, otherwise the agent's profile fields as before.
      agency_name: (branding ? branding.agency_name : agentProfile?.agency_name) || undefined,
      agency_logo_url:
        (branding ? branding.agency_logo_url : agentProfile?.agency_logo_url) || undefined,
      is_independent: agentProfile?.is_independent || undefined
    },
    // Amenity fields (snake_case DB → camelCase frontend)
    pool: listing.pool ?? false,
    gym: listing.gym ?? false,
    garage: listing.garage ?? false,
    garden: listing.garden ?? false,
    security: listing.security ?? false,
    airCon: listing.air_con ?? false,
    balcony: listing.balcony ?? false,
    furnished: listing.furnished ?? false,
    fireplace: listing.fireplace ?? false,
    borehole: listing.borehole ?? false,
    nearTarmac: listing.near_tarmac ?? false,
    titleReady: listing.title_ready ?? false,
    staffQuarters: listing.staff_quarters ?? false,
    backupGenerator: listing.backup_generator ?? false,
    // Trust & Legal
    titleDeedType: listing.title_deed_type || null,
    leaseholdYearsRemaining: listing.leasehold_years_remaining ?? null,
    encumbranceFree: listing.encumbrance_free || false,
    // Security
    gatedCommunity: listing.gated_community || false,
    electricFence: listing.electric_fence || false,
    cctv: listing.cctv || false,
    securityGuards: listing.security_guards || false,
    alarmSystem: listing.alarm_system || false,
    // Water
    mainsWater: listing.mains_water || false,
    waterStorageLitres: listing.water_storage_litres ?? null,
    // Power
    solar: listing.solar || false,
    // Connectivity
    fiberInternet: listing.fiber_internet || false,
    // Building
    floorLevel: listing.floor_level ?? null,
    parkingSpaces: listing.parking_spaces ?? null,
    lift: listing.lift || false,
    enSuite: listing.en_suite || false,
    kidsPlayArea: listing.kids_play_area || false,
    // Land
    roadAccessType: listing.road_access_type || null,
    distanceFromTarmacM: listing.distance_from_tarmac_m ?? null,
    zoning: listing.zoning || null,
    beaconed: listing.beaconed || false,
    surveyApproved: listing.survey_approved || false,
    // Service charge for monthly cost calculator
    serviceCharge: listing.service_charge || undefined,
    // Sold fields
    finalPrice: listing.final_price || undefined,
    soldDate: listing.sold_at || undefined,
    daysOnMarket: listing.days_on_market || undefined,
    createdAt: listing.created_at || undefined,
    // New Development fields
    isParentListing: listing.isParentListing || listing.property_type === 'new_development',
    units: listing.units || undefined,
  };
}

/**
 * Omvandla DbProfile till User (för frontend)
 */
export function dbProfileToUser(profile: DbProfile, email?: string): User {
  return {
    id: profile.id,
    slug: profile.slug || undefined,
    email: profile.email || email || '',
    role: profile.role,
    name: profile.full_name || 'User',
    phone: profile.phone || undefined,
    tier: profile.tier || undefined,
    isVerified: profile.is_verified,
    avatarUrl: profile.avatar_url || undefined,
    backgroundImage: profile.background_image || undefined,
    agencyId: profile.agency_id || undefined,
    agencyName: profile.agency_name || undefined,
    agencyLogoUrl: profile.agency_logo_url || undefined,
    isIndependent: profile.is_independent || undefined,
    location: profile.location || undefined,
    bio: profile.bio || undefined,
    // Subscription lifecycle fields
    subscriptionExpiresAt: profile.subscription_expires_at || undefined,
    subscriptionStatus: profile.subscription_status || undefined,
    welcomeGrantedAt: profile.welcome_granted_at || undefined,
    createdAt: profile.created_at || undefined,
    renewalReminderSent: profile.renewal_reminder_sent || undefined,
    spotlightCredits: profile.spotlight_credits || undefined
  };
}